import React, { useEffect, useMemo, useRef, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api, API_BASE } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { fmt } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, BadgePercent, Upload, Download, Building2, Layers } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = { name_en: "", name_ar: "", sku: "", category_id: "", category: "", price: 0, sale_price: "", cost: 0, stock: 0, low_stock_threshold: 5, active: true, show_on_website: true, is_featured: false, image_url: "" };
const PAGE_SIZE = 50;

export default function Products() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const { branches, activeBranchId } = useBranch();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  // Per-branch breakdown popover (product_id whose breakdown is open).
  const [breakdownFor, setBreakdownFor] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const branchLabel = (b) => (lang === "ar" ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar)) || b.branch_id;

  const load = async () => {
    // Branch-scoped product list — `stock` field reflects the active branch.
    const [p, c] = await Promise.all([
      api.get(activeBranchId ? `/products?branch_id=${encodeURIComponent(activeBranchId)}` : "/products"),
      api.get("/categories?type=product"),
    ]);
    setItems(p.data); setCategories(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeBranchId]);
  useEffect(() => { setPage(1); }, [search]);

  const openBreakdown = async (pid) => {
    if (breakdownFor === pid) { setBreakdownFor(null); return; }
    setBreakdownFor(pid);
    setBreakdown(null);
    try {
      const r = await api.get(`/products/${pid}/stock-by-branch`);
      setBreakdown(r.data);
    } catch (e) {
      setBreakdown({ branches: [] });
    }
  };

  const catName = (catId, fallback) => {
    const c = categories.find(x => x.category_id === catId);
    return c ? (lang === "ar" ? c.name_ar : c.name_en) : (fallback || "-");
  };

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const cat = catName(p.category_id, p.category) || "";
      return (
        (p.name_en || "").toLowerCase().includes(q) ||
        (p.name_ar || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        cat.toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line
  }, [items, search, categories, lang]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredItems, page],
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const submit = async () => {
    try {
      const cat = categories.find(c => c.category_id === form.category_id);
      const payload = {
        ...form,
        category: cat ? (lang === "ar" ? cat.name_ar : cat.name_en) : (form.category || "general"),
        category_id: form.category_id || null,
        price: parseFloat(form.price) || 0,
        sale_price: form.sale_price === "" || form.sale_price == null ? null : parseFloat(form.sale_price),
        cost: parseFloat(form.cost) || 0,
        stock: parseInt(form.stock) || 0,
        low_stock_threshold: parseInt(form.low_stock_threshold) || 5,
      };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(t("saved")); setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error(t("error_generic")); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "هل أنت متأكد من الحذف؟" : "Delete this product?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      const r = await api.delete(`/products/${id}`);
      const mode = r?.data?.mode;
      const refs = r?.data?.references || {};
      if (mode === "soft") {
        const total = (refs.invoices || 0) + (refs.purchases || 0) + (refs.packages || 0);
        toast.success(
          lang === "ar"
            ? `تم تعطيل المنتج (مرتبط بـ ${total} مرجع: ${refs.invoices || 0} فواتير، ${refs.purchases || 0} مشتريات، ${refs.packages || 0} باقات). البيانات التاريخية محفوظة.`
            : `Product disabled (linked to ${total} records: ${refs.invoices || 0} invoices, ${refs.purchases || 0} purchases, ${refs.packages || 0} packages). History preserved.`,
          { duration: 6000 }
        );
      } else {
        toast.success(lang === "ar" ? "تم حذف المنتج نهائياً" : "Product deleted");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "ar" ? "فشل الحذف" : "Delete failed"));
    }
  };

  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${API_BASE}/products/template`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "products_template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error(t("error_generic")); }
  };

  const exportExcel = async () => {
    try {
      const r = await fetch(`${API_BASE}/products/export`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `products_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(lang === "ar" ? `تم تصدير ${items.length} منتج` : `Exported ${items.length} products`);
    } catch { toast.error(lang === "ar" ? "فشل التصدير" : "Export failed"); }
  };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created} ${t("imported_count")}, ${r.data.skipped} ${t("skipped_count")}`);
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-4xl gold-text">{t("nav_products")}</h1>
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={downloadTemplate} data-testid="download-template-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Download size={14}/>{t("download_template")}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" data-testid="import-file"/>
          <button onClick={() => fileRef.current?.click()} data-testid="import-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Upload size={14}/>{t("import_excel")}
          </button>
          <button onClick={exportExcel} data-testid="products-export-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Download size={14}/>{lang === "ar" ? "تصدير" : "Export"}
          </button>
          <button onClick={() => { setForm({ ...empty }); setEditId(null); setOpen(true); }} data-testid="add-product-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2">
            <Plus size={16}/>{t("add")}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          className="input-dark max-w-sm"
          placeholder={lang === "ar" ? "ابحث بالاسم أو SKU أو الفئة..." : "Search by name, SKU or category..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="products-search"
        />
        {filteredItems.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm text-[var(--text-muted)]">
              {lang === "ar"
                ? `عرض ${Math.min((page - 1) * PAGE_SIZE + 1, filteredItems.length)}-${Math.min(page * PAGE_SIZE, filteredItems.length)} من ${filteredItems.length}`
                : `Showing ${Math.min((page - 1) * PAGE_SIZE + 1, filteredItems.length)}-${Math.min(page * PAGE_SIZE, filteredItems.length)} of ${filteredItems.length}`}
            </div>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-rose-outline px-3 py-2 rounded-md text-sm disabled:opacity-50">{lang === "ar" ? "السابق" : "Prev"}</button>
            <span className="text-sm text-[var(--text-muted)]">{page}/{totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-rose-outline px-3 py-2 rounded-md text-sm disabled:opacity-50">{lang === "ar" ? "التالي" : "Next"}</button>
          </div>
        )}
      </div>

      <div className="card-surface overflow-auto">
        {activeBranchId && branches.length > 1 && (
          <div className="px-4 py-2 text-xs text-[var(--text-muted)] bg-[var(--gold-tint)] border-b border-[var(--border-subtle)] flex items-center gap-2">
            <Building2 size={12} className="text-[var(--gold)]"/>
            {lang === "ar" ? "الكميات المعروضة هي مخزون فرعك النشط فقط — اضغط أيقونة الطبقات لرؤية التوزيع على كل الفروع." : "Quantities shown are for your active branch only — click the layers icon for a per-branch breakdown."}
          </div>
        )}
        <table className="table-dark">
          <thead>
            <tr><th>{t("name")}</th><th>{t("sku")}</th><th>{t("category")}</th><th>{t("price")}</th><th>{t("sale_price")}</th><th>{activeBranchId ? (lang === "ar" ? "مخزون الفرع" : "Branch stock") : t("stock")}</th><th>{lang === "ar" ? "الموقع" : "Web"}</th><th>{t("actions")}</th></tr>
          </thead>
          <tbody>
            {pageItems.map(p => (
              <React.Fragment key={p.product_id}>
              <tr>
                <td className="text-[var(--text)]">{lang === "ar" ? p.name_ar : p.name_en}</td>
                <td className="text-[var(--text-muted)]">{p.sku}</td>
                <td className="text-[var(--text-muted)]">{catName(p.category_id, p.category)}</td>
                <td className={`${p.sale_price ? "line-through text-[var(--text-muted)]" : "gold-text"}`}>{p.price}</td>
                <td>{p.sale_price ? <span className="gold-text font-medium flex items-center gap-1"><BadgePercent size={12}/>{p.sale_price}</span> : <span className="text-[var(--text-disabled)]">-</span>}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className={`badge ${p.stock <= p.low_stock_threshold ? "badge-red" : "badge-green"}`} data-testid={`product-stock-${p.product_id}`}>{p.stock}</span>
                    {branches.length > 1 && (
                      <button onClick={() => openBreakdown(p.product_id)} className="p-1 rounded hover:bg-[var(--gold-tint)] text-[var(--gold)]" title={lang === "ar" ? "توزيع على الفروع" : "Per-branch breakdown"} data-testid={`product-breakdown-${p.product_id}`}>
                        <Layers size={12}/>
                      </button>
                    )}
                  </div>
                </td>
                <td><span className={`badge ${p.show_on_website !== false ? "badge-green" : ""}`}>{p.show_on_website !== false ? "✓" : "✗"}</span></td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => { setForm({ ...empty, ...p, sale_price: p.sale_price ?? "" }); setEditId(p.product_id); setOpen(true); }} data-testid={`edit-product-${p.product_id}`} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)]"><Pencil size={14}/></button>
                    <button onClick={() => remove(p.product_id)} data-testid={`delete-product-${p.product_id}`} className="p-1.5 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
              {breakdownFor === p.product_id && (
                <tr>
                  <td colSpan={8} className="bg-[var(--bg)] p-0">
                    <div className="p-3" data-testid={`product-breakdown-panel-${p.product_id}`}>
                      <div className="text-xs text-[var(--text-muted)] mb-2 flex items-center gap-1">
                        <Layers size={12} className="text-[var(--gold)]"/>
                        {lang === "ar" ? "الكميات حسب الفرع" : "Stock by branch"}
                      </div>
                      {!breakdown && <div className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "جارٍ التحميل..." : "Loading..."}</div>}
                      {breakdown && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {(breakdown.branches || []).map((b) => {
                            const low = b.qty <= (breakdown.low_stock_threshold || 5);
                            return (
                              <div key={b.branch_id} className="bg-[var(--surface)] border border-[var(--border-subtle)] rounded-md p-2 flex items-center justify-between">
                                <div className="text-xs">
                                  <div className="flex items-center gap-1 text-[var(--text)]">
                                    <Building2 size={10} className="text-[var(--gold)]"/>
                                    {lang === "ar" ? b.branch_name_ar : b.branch_name_en}
                                    {b.is_default && <span className="text-[var(--gold)]">★</span>}
                                  </div>
                                </div>
                                <span className={`badge num-display ${low ? "badge-red" : "badge-green"}`}>{b.qty}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
            {pageItems.length === 0 && <tr><td colSpan={8} className="text-center text-[var(--text-muted)] py-8">{search ? (lang === "ar" ? "لا نتائج تطابق البحث" : "No results match your search") : t("no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4"
          onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}
        >
          <div className="card-surface p-6 w-full max-w-xl space-y-4 max-h-[92vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{editId ? t("edit") : t("add")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-[var(--text-muted)]">{t("name_en")}</label><input className="input-dark" value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("name_ar")}</label><input className="input-dark" value={form.name_ar} onChange={e => setForm({...form, name_ar: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("sku")}</label><input className="input-dark" value={form.sku} onChange={e => setForm({...form, sku: e.target.value})}/></div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("category")}</label>
                <select className="input-dark" value={form.category_id || ""} onChange={e => setForm({...form, category_id: e.target.value})} data-testid="product-category-select">
                  <option value="">{t("select_category")}</option>
                  {categories.map(c => <option key={c.category_id} value={c.category_id}>{lang === "ar" ? c.name_ar : c.name_en}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("regular_price")}</label><input className="input-dark" type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><BadgePercent size={12}/>{t("sale_price")}</label><input className="input-dark" type="number" value={form.sale_price} onChange={e => setForm({...form, sale_price: e.target.value})} data-testid="product-sale-price" placeholder="-"/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("cost")} ({t("avg_cost")})</label><input className="input-dark" type="number" value={form.cost} onChange={e => setForm({...form, cost: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("stock")}</label><input className="input-dark" type="number" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">Min stock</label><input className="input-dark" type="number" value={form.low_stock_threshold} onChange={e => setForm({...form, low_stock_threshold: e.target.value})}/></div>
              <div className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})}/><span className="text-sm text-[var(--text)]">{t("active")}</span></div>
              <div className="flex items-center gap-2 pt-5"><input type="checkbox" data-testid="product-show-on-website" checked={form.show_on_website !== false} onChange={e => setForm({...form, show_on_website: e.target.checked})}/><span className="text-sm text-[var(--text)]">{lang === "ar" ? "إظهار على الموقع" : "Show on Website"}</span></div>
              <div className="flex items-center gap-2 pt-5"><input type="checkbox" data-testid="product-is-featured" checked={!!form.is_featured} onChange={e => setForm({...form, is_featured: e.target.checked})}/><span className="text-sm text-[var(--text)]">{lang === "ar" ? "منتج مميز" : "Featured"}</span></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)] mb-1 block">{lang === "ar" ? "صورة المنتج" : "Product Image"}</label><ImageUpload value={form.image_url || ""} onChange={(url) => setForm({...form, image_url: url})} recommendedSize="800×800" category="products" aspect="aspect-square" testIdPrefix="product-image" lang={lang} /></div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-[var(--text)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="product-save-btn" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
